import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Icon,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../../axios";
import usePageTitle from "../../pageTitle";
import AppConfig from "../../../appConfig";

function LoyaltyPromotionPage() {
  usePageTitle("Consumer Loyalty");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [promotion, setPromotion] = useState(null);
  const [products, setProducts] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const showMessage = (message, severity = "error") => {
    setSnackbarMessage(message || "Unable to complete request.");
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchPromotion = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          `web/tm/loyalty/promotions/${ref}/fetch.json`,
        );

        if (response.data.status !== "success") {
          showMessage(response.data.data?.message || "Promotion not found.");
          return;
        }

        const data = response.data.data;

        setPromotion(data.promotion || null);
        setProducts(data.products || []);
      } catch (error) {
        showMessage(
          error.response?.data?.data?.message ||
            "Unable to load this promotion.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (ref) fetchPromotion();
  }, [ref]);

  const cardSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "0 2px 12px rgba(0,0,0,0.035)",
    bgcolor: "background.paper",
  };

  const getImageUrl = (image) => {
    if (!image) return "";

    // if (/^https?:\/\//i.test(image)) return image;

    // return image.startsWith("/") ? image : `/media/${image}`;

    return `${AppConfig.api_media_url}${image}`;
  };

  const submitPurchaseClaim = () => {
    if (!promotion?.reference) {
      showMessage("Promotion information is unavailable.");
      return;
    }

    navigate(`/tm/loyalty/claim/${promotion.reference}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f6f8",
        color: "#17191d",
        pb: { xs: 4, md: 7 },
      }}
    >
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "rgba(18,20,24,0.45)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          height: 8,
          background: "linear-gradient(90deg, #17191d 0%, #575b64 100%)",
        }}
      />

      <Box
        sx={{
          background:
            "radial-gradient(ellipse at 90% 0%, rgba(255,255,255,0.10), transparent 38%), linear-gradient(125deg, #17191d 0%, #292d35 100%)",
          color: "#fff",
          px: { xs: 2.5, md: 5 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ mb: 4 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                color: "#17191d",
                fontWeight: 800,
                fontSize: 18,
              }}
            >
              <Icon>loyalty</Icon>
            </Box>

            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.2 }}
            >
              Consumer Loyalty Program
            </Typography>
          </Stack>

          <Typography
            variant="overline"
            sx={{
              color: "rgba(255,255,255,0.65)",
              letterSpacing: 2,
              fontWeight: 600,
            }}
          >
            {promotion?.title || "Loyalty promotion"}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              mt: 1,
              maxWidth: 760,
              fontWeight: 700,
              letterSpacing: { xs: -0.7, md: -1.4 },
              lineHeight: 1.15,
              fontSize: { xs: "2rem", sm: "2.6rem", md: "3rem" },
              overflowWrap: "anywhere",
            }}
          >
            Earn rewards when you buy.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              maxWidth: 650,
              lineHeight: 1.8,
              color: "rgba(255,255,255,0.72)",
              whiteSpace: "pre-line",
            }}
          >
            {promotion?.description ||
              "Purchase an eligible product and submit your purchase for validation to participate in this loyalty promotion."}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 3, flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              size="small"
              label="PRODUCT LOYALTY"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            />

            <Chip
              size="small"
              label={`${products.length} eligible ${
                products.length === 1 ? "product" : "products"
              }`}
              sx={{
                color: "rgba(255,255,255,0.85)",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.16)",
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: { xs: 2, md: 4 },
          mt: { xs: -2.5, md: -3 },
        }}
      >
        {promotion && (
          <Stack spacing={2.5}>
            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.75 } }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    bgcolor: "#e7f5ec",
                    color: "#267446",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 21,
                    fontWeight: 700,
                  }}
                >
                  ✓
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    How to participate
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, lineHeight: 1.8 }}
                  >
                    Select an eligible product, enter your unique code or
                    register to participate, then submit a photo of the product
                    together with your purchase receipt or other purchase
                    evidence. Your submission will be processed for validation.
                  </Typography>
                </Box>
              </Stack>
            </Card>

            <Box>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 1.4, fontWeight: 700 }}
              >
                ELIGIBLE PRODUCTS
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Select from the products participating in this promotion.
              </Typography>
            </Box>

            {products.length === 0 ? (
              <Card sx={{ ...cardSx, p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  There are currently no eligible products available for this
                  promotion.
                </Typography>
              </Card>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {products.map((product) => (
                  <Card
                    key={product.reference}
                    sx={{
                      ...cardSx,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {product.image ? (
                      <Box
                        component="img"
                        src={getImageUrl(product.image)}
                        alt={product.title}
                        sx={{
                          width: "100%",
                          height: 210,
                          objectFit: "contain",
                          bgcolor: "#fff",
                          p: 2,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 170,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "#f0f1f3",
                          color: "#777c85",
                          fontSize: 14,
                        }}
                      >
                        Product image unavailable
                      </Box>
                    )}

                    <Divider />

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {product.title}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>
            )}

            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Claim loyalty points?
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, lineHeight: 1.7 }}
                  >
                    Take a picture of your receipt and upload to claim your
                    points.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={submitPurchaseClaim}
                  disabled={products.length === 0}
                  endIcon={<span>→</span>}
                  sx={{
                    minWidth: { sm: 230 },
                    minHeight: 48,
                    borderRadius: 1.5,
                    px: 3,
                    bgcolor: "#202329",
                    color: "#fff",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#363a43",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#d7d9dd",
                      color: "#858991",
                    },
                  }}
                >
                  Claim Points
                </Button>
              </Stack>
            </Card>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ px: 2, lineHeight: 1.7 }}
            >
              Purchase submissions are subject to validation. Ensure your
              product photo and purchase evidence are clear and readable.
            </Typography>
          </Stack>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LoyaltyPromotionPage;
